import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  Form,
  Heading,
  PasswordField,
  Text,
  TextField,
} from "ui-components";

import { makeToast } from "@/src/components/Toast/Toast";
import {
  UserResponseEntity,
  useUserChangePassword,
} from "@/src/hooks/api/generated";

import {
  EmailAndPasswordValidationSchemaType,
  emailAndPasswordValidationSchema,
} from "../schema/my-account.schema";

interface IUpdatePasswordFormProps {
  userData: UserResponseEntity;
}

export function UpdatePasswordForm({
  userData,
}: IUpdatePasswordFormProps): JSX.Element {
  const { mutate: changePassword, status } = useUserChangePassword();

  const {
    reset,
    register,
    handleSubmit,
    getFieldState,
    formState: { errors },
  } = useForm<EmailAndPasswordValidationSchemaType>({
    resolver: zodResolver(emailAndPasswordValidationSchema),
  });

  const onSubmit: SubmitHandler<EmailAndPasswordValidationSchemaType> = (
    data
  ) => {
    changePassword(
      {
        data: {
          newPassword: data.password,
          newPasswordConfirmation: data.password_confirmation,
        },
      },
      {
        onSuccess: () => {
          reset();
          makeToast({
            title: "Password updated successfully.",
            intent: "success",
          });
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  };

  return (
    <div className="flex flex-col items-center gap-y-10">
      <Heading variant="h3">Update your email and password</Heading>
      <Form
        className="flex flex-col gap-y-5 w-[500px]"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField label="Email" isRequired isDisabled value={userData.email} />
        <PasswordField
          label="Type your new password"
          isRequired
          register={register("password")}
          errorMessage={errors.password?.message}
          hasValue={getFieldState("password").isDirty}
        />
        <PasswordField
          label="Confirm your new password"
          isRequired
          register={register("password_confirmation")}
          errorMessage={errors.password_confirmation?.message}
          hasValue={getFieldState("password_confirmation").isDirty}
        />
        <Button
          type="submit"
          size="full"
          className="mt-5"
          isLoading={status === "pending"}
          isDisabled={status === "pending"}
        >
          <Text>Save changes</Text>
        </Button>
      </Form>
    </div>
  );
}
